import secureApiService from './secureApiService'
import {
  CourseJSON,
  CourseModel,
  CourseModuleJSON,
  CourseModuleModel,
  CourseResourceJSON,
  CourseResourceModel,
} from '@/models/course.model'
import { PaginatedResponse, fetchAllPages } from '@/models/templates/pagination.model'

export interface CourseQueryParams {
  page?: number
  limit?: number
  facultyId?: string
  departmentId?: string
  programmeId?: string
  instructorId?: string
  isActive?: boolean
  level?: string
  semester?: string
  search?: string
}

export interface CourseMutationPayload {
  programmeId: string
  title: string
  description?: string | null
  code?: string | null
  credits?: number | null
  instructorId?: string | null
  startDate?: string | null
  endDate?: string | null
  departmentId?: string | null
  level?: string | null
  semester?: string | null
  is_active?: boolean | null
}

export interface CourseModuleMutationPayload {
  title: string
  description?: string | null
  order?: number | null
  is_active?: boolean | null
}

export interface CourseResourceUploadPayload {
  title: string
  description?: string | null
  type?: string | null
  files: File[]
  moduleId?: string | null
  order?: number | null
}

export interface CourseResourceLinkPayload {
  videoId: string
  playbackUrl: string
  title: string
  mimeType?: string | null
  size?: number | null
  order?: number | null
  moduleId?: string | null
}

interface CourseCollectionResponseJSON {
  success: boolean
  message?: string
  data?: CourseJSON[] | CourseJSON | null
  meta?: {
    total?: number
    page?: number
    perPage?: number
  }
}

interface CourseSingleResponseJSON {
  success: boolean
  message?: string
  data?: CourseJSON | null
}

interface CourseModuleResponseJSON {
  success: boolean
  message?: string
  data?: CourseModuleJSON | CourseModuleJSON[] | null
}

interface CourseResourceResponseJSON {
  success?: boolean
  message?: string
  data?: CourseResourceJSON | CourseResourceJSON[] | null
}

class CoursesService {
  private basePath = '/courses'

  async getCoursesPage(params: CourseQueryParams = {}): Promise<PaginatedResponse<CourseModel>> {
    const searchParams = new URLSearchParams()

    if (params.page) searchParams.append('page', String(params.page))
    if (params.limit) searchParams.append('limit', String(params.limit))
    if (params.facultyId) searchParams.append('facultyId', params.facultyId)
    if (params.departmentId) searchParams.append('departmentId', params.departmentId)
    if (params.programmeId) searchParams.append('programmeId', params.programmeId)
    if (params.instructorId) searchParams.append('instructorId', params.instructorId)
    if (typeof params.isActive === 'boolean') searchParams.append('isActive', String(params.isActive))
    if (params.level) searchParams.append('level', params.level)
    if (params.semester) searchParams.append('semester', params.semester)
    if (params.search) searchParams.append('search', params.search)

    const queryString = searchParams.toString()
    const endpoint = queryString ? `${this.basePath}?${queryString}` : this.basePath

    const response = await secureApiService.get<CourseCollectionResponseJSON>(endpoint, {
      requireAuth: true,
    })

    const payload = response.data
    if (!payload?.success) {
      throw new Error(payload?.message || 'Unable to fetch courses')
    }

    const rawData = Array.isArray(payload.data)
      ? payload.data
      : payload.data
        ? [payload.data]
        : []

    const data = rawData
      .filter((course): course is CourseJSON => Boolean(course))
      .map((course) => CourseModel.fromJSON(course))

    const meta = {
      total: payload.meta?.total ?? rawData.length,
      page: payload.meta?.page ?? params.page ?? 1,
      perPage: payload.meta?.perPage ?? params.limit ?? (rawData.length || 25),
    }

    return { data, meta }
  }

  async getAllCourses(params: CourseQueryParams = {}): Promise<PaginatedResponse<CourseModel>> {
    const limit = params.limit ?? 50
    return fetchAllPages((page) => this.getCoursesPage({ ...params, page, limit }), limit)
  }

  async getCourse(courseId: string): Promise<CourseModel> {
    const response = await secureApiService.get<CourseSingleResponseJSON>(`${this.basePath}/${courseId}`, {
      requireAuth: true,
    })

    const payload = response.data
    if (!payload?.success || !payload.data) {
      throw new Error(payload?.message || 'Unable to fetch course details')
    }

    return CourseModel.fromJSON(payload.data)
  }

  async createCourse(payload: CourseMutationPayload): Promise<CourseModel> {
    const response = await secureApiService.post<CourseSingleResponseJSON>(this.basePath, payload)

    const body = response.data
    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to create course')
    }

    return CourseModel.fromJSON(body.data)
  }

  async updateCourse(courseId: string, payload: CourseMutationPayload): Promise<CourseModel> {
    const response = await secureApiService.put<CourseSingleResponseJSON>(`${this.basePath}/${courseId}`, payload)

    const body = response.data
    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to update course')
    }

    return CourseModel.fromJSON(body.data)
  }

  async deleteCourse(courseId: string): Promise<void> {
    const response = await secureApiService.delete<CourseSingleResponseJSON>(`${this.basePath}/${courseId}`)
    const body = response.data
    if (!body?.success) {
      throw new Error(body?.message || 'Unable to delete course')
    }
  }

  async getModules(courseId: string): Promise<CourseModuleModel[]> {
    const response = await secureApiService.get<CourseModuleResponseJSON>(
      `${this.basePath}/${courseId}/modules`,
      { requireAuth: true },
    )

    const body = response.data
    if (!body?.success) {
      throw new Error(body?.message || 'Unable to fetch modules')
    }

    const modulesArray = Array.isArray(body.data)
      ? body.data
      : body.data
        ? [body.data]
        : []

    return modulesArray
      .filter((module): module is CourseModuleJSON => Boolean(module))
      .map((module) => CourseModuleModel.fromJSON(module))
  }

  async getResources(
    courseId: string,
    params: { moduleId?: string } = {},
  ): Promise<CourseResourceModel[]> {
    const searchParams = new URLSearchParams()
    if (params.moduleId) {
      searchParams.set('moduleId', params.moduleId)
    }

    const query = searchParams.toString()
    const endpoint = query
      ? `${this.basePath}/${courseId}/resources?${query}`
      : `${this.basePath}/${courseId}/resources`

    const response = await secureApiService.get<CourseResourceResponseJSON>(endpoint, {
      requireAuth: true,
    })

    const body = response.data
    if (body?.success === false) {
      throw new Error(body?.message || 'Unable to fetch course resources')
    }

    const resourcePayload = Array.isArray(body?.data)
      ? body?.data
      : body?.data
        ? [body.data]
        : []

    return resourcePayload
      .filter((resource): resource is CourseResourceJSON => Boolean(resource))
      .map((resource) => CourseResourceModel.fromJSON(resource))
  }

  async createModule(courseId: string, payload: CourseModuleMutationPayload): Promise<CourseModuleModel> {
    const response = await secureApiService.post<CourseModuleResponseJSON>(
      `${this.basePath}/${courseId}/modules`,
      payload,
    )

    const body = response.data
    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to create module')
    }

    const moduleJson = Array.isArray(body.data) ? body.data[0] : body.data
    if (!moduleJson) {
      throw new Error('Course module payload missing in response')
    }

    return CourseModuleModel.fromJSON(moduleJson)
  }

  async updateModule(courseId: string, moduleId: string, payload: CourseModuleMutationPayload): Promise<CourseModuleModel> {
    const response = await secureApiService.put<CourseModuleResponseJSON>(
      `${this.basePath}/${courseId}/modules/${moduleId}`,
      payload,
    )

    const body = response.data
    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to update module')
    }

    const moduleJson = Array.isArray(body.data) ? body.data[0] : body.data
    if (!moduleJson) {
      throw new Error('Course module payload missing in response')
    }

    return CourseModuleModel.fromJSON(moduleJson)
  }

  async deleteModule(courseId: string, moduleId: string): Promise<void> {
    const response = await secureApiService.delete<CourseModuleResponseJSON>(
      `${this.basePath}/${courseId}/modules/${moduleId}`,
    )

    const body = response.data
    if (!body?.success) {
      throw new Error(body?.message || 'Unable to delete module')
    }
  }

  async uploadResources(courseId: string, payload: CourseResourceUploadPayload): Promise<CourseResourceModel[]> {
    const formData = new FormData()
    formData.append('title', payload.title)
    formData.append('type', payload.type ?? 'document')
    if (payload.description) {
      formData.append('description', payload.description)
      formData.append('notes', payload.description)
    }
    if (payload.moduleId) formData.append('moduleId', payload.moduleId)
    if (typeof payload.order === 'number') formData.append('order', String(payload.order))

    payload.files.forEach((file) => {
      formData.append('files', file)
    })

    const response = await secureApiService.postFormData<CourseResourceResponseJSON>(
      `${this.basePath}/${courseId}/resources`,
      formData
    )

    const body = response.data
    if (body?.success === false) {
      throw new Error(body?.message || 'Unable to upload resources')
    }

    const resourceData = Array.isArray(body?.data)
      ? body?.data
      : body?.data
        ? [body.data]
        : []

    return resourceData
      .filter((resource): resource is CourseResourceJSON => Boolean(resource))
      .map((resource) => CourseResourceModel.fromJSON(resource))
  }

  async linkResource(courseId: string, payload: CourseResourceLinkPayload): Promise<CourseResourceModel> {
    const response = await secureApiService.post<CourseResourceResponseJSON>(
      `${this.basePath}/${courseId}/resources/link`,
      payload,
    )

    const body = response.data
    const resourcePayload =
      Array.isArray(body?.data) ? body?.data[0] : body?.data ?? (body as unknown as CourseResourceJSON | undefined)

    if (!resourcePayload) {
      throw new Error(body?.message || 'Unable to link resource')
    }

    return CourseResourceModel.fromJSON(resourcePayload)
  }

  async deleteResource(courseId: string, resourceId: string): Promise<void> {
    const response = await secureApiService.delete<CourseResourceResponseJSON>(
      `${this.basePath}/${courseId}/resources/${resourceId}`,
    )

    const body = response.data
    if (body?.success === false) {
      throw new Error(body?.message || 'Unable to delete resource')
    }
  }
}

export const coursesService = new CoursesService()
